import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import { GOOGLE_OAUTH_CLIENT_ID_WEB } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import StatusContainer from '@/components/StatusContainer';

let scriptPromise;
let initializedClient;
let activeButton;
let nextButtonId = 0;

function loadGoogleClient() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById('google-gsi-script');
    const isNew = !script;
    if (isNew) {
      script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
    }
    const finish = (loaded) => {
      clearTimeout(timeout);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      if (loaded && window.google?.accounts?.id) {
        resolve(window.google.accounts.id);
      } else {
        script.remove();
        reject(new Error('Google sign-in could not load'));
      }
    };
    const onLoad = () => finish(true);
    const onError = () => finish(false);
    const timeout = setTimeout(onError, 15000);
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    if (isNew) document.head.appendChild(script);
  }).then(client => {
    scriptPromise = undefined;
    return client;
  }).catch(error => {
    scriptPromise = undefined;
    throw error;
  });
  return scriptPromise;
}

function initializeGoogleClient(client) {
  if (initializedClient === client) return;
  client.initialize({
    client_id: GOOGLE_OAUTH_CLIENT_ID_WEB,
    ux_mode: 'popup',
    // An explicit sign-in click must work even when One Tap/FedCM fails or is
    // suppressed. FedCM display-moment notifications cannot drive a fallback.
    // https://developers.google.com/identity/gsi/web/guides/fedcm-migration
    use_fedcm_for_button: false,
    auto_select: false,
    callback: response => {
      const button = activeButton;
      if (!button || response?.state !== button.state) return;
      activeButton = null;
      button.receive(response);
    },
  });
  initializedClient = client;
}

export default function GoogleWebSignInButton({ onIdToken, onStart, disabled, mode }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const container = useRef(null);
  const latest = useRef({ onIdToken, onStart, disabled });
  latest.current = { onIdToken, onStart, disabled };
  const [status, setStatus] = useState('loading');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let disposed = false;
    let observer;
    const element = container.current;
    const button = {
      state: `gab-google-${++nextButtonId}`,
      receive: response => {
        if (disposed || latest.current.disabled) return;
        if (response?.credential) latest.current.onIdToken(response.credential);
        else setStatus('error');
      },
    };
    setStatus('loading');

    (async () => {
      try {
        if (!GOOGLE_OAUTH_CLIENT_ID_WEB) throw new Error('Google client ID is missing');
        const client = await loadGoogleClient();
        if (disposed) return;
        initializeGoogleClient(client);
        let previousWidth;
        const render = () => {
          if (disposed) return;
          const width = Math.min(400, Math.floor(element.getBoundingClientRect().width));
          if (width <= 0 || width === previousWidth) return;
          previousWidth = width;
          element.replaceChildren();
          client.renderButton(element, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: mode === 'signup' ? 'signup_with' : 'signin_with',
            width,
            state: button.state,
            click_listener: () => {
              if (disposed || latest.current.disabled) return;
              // GIS has one page-wide credential callback. Route it to the
              // clicked form, including account verification and login modals.
              activeButton = button;
              latest.current.onStart?.();
            },
          });
          setStatus('ready');
        };
        render();
        if (typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(render);
          observer.observe(element);
        }
      } catch (_) {
        if (!disposed) setStatus('error');
      }
    })();

    return () => {
      disposed = true;
      observer?.disconnect();
      if (activeButton === button) activeButton = null;
      element.replaceChildren();
    };
  }, [mode, retry]);

  return (
    <div style={{ width: '100%' }}>
      {status === 'error' && (
        <>
          <StatusContainer type="error" message={t('auth.sso.errors.googleFailed')} />
          <Pressable onPress={() => setRetry(value => value + 1)} disabled={disabled}
            accessibilityRole="button" style={{ padding: 12, alignItems: 'center' }}>
            <Text>{t('common.retry')}</Text>
          </Pressable>
        </>
      )}
      <div inert={disabled || status !== 'ready' ? true : undefined}
        aria-busy={disabled || status === 'loading'}
        style={{ position: 'relative', minHeight: status === 'error' ? 0 : 44, display: status === 'error' ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div ref={container} style={{ width: '100%', maxWidth: 400, opacity: disabled ? 0.5 : 1 }} />
        {(status === 'loading' || disabled) && (
          <div style={{ position: 'absolute' }}>
            <ActivityIndicator size="small" color={colors.text_color_primary} accessibilityLabel={t('common.loading')} />
          </div>
        )}
      </div>
    </div>
  );
}
